/**
 * Forum-side preference state.
 *
 * Holds the current reading-text scale and interface-size mode, reads/writes
 * the two cookies that persist them, derives the admin-configured defaults
 * from the forum payload, and (re)applies the generated stylesheets.
 */
import app from 'flarum/forum/app';
import { COOKIE_TEXT, COOKIE_UI, TEXT_MIN, BASE_DEFAULTS, clampScale, clampBase } from './constants';
import { applyTextScale, applyTextBases, applyUiScale } from './styles';
export const state = {
  textScale: TEXT_MIN,
  uiLarge: false
};
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}
function setCookie(name, value) {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  // Add `Secure` on HTTPS pages so the cookie is never sent over plain HTTP.
  // The payload isn't sensitive (a percentage or 'large'/'default'), but it's
  // standard hygiene and costs nothing; on http dev sites we omit it so the
  // cookie still applies.
  let secure = '';
  try {
    if (typeof location !== 'undefined' && location.protocol === 'https:') {
      secure = '; Secure';
    }
  } catch (e) {
    // location unavailable — leave Secure off.
  }
  document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires.toUTCString() + '; path=/; SameSite=Lax' + secure;
}

/**
 * Read a forum attribute. During `app.initializers` (when we first apply
 * styles to avoid a flash) core hasn't built `app.forum` from the payload yet,
 * so fall back to reading the bootstrap payload directly. Once booted (e.g. the
 * modal's "reset to default"), `app.forum.attribute()` is used.
 */
function forumAttribute(name) {
  var _resources, _app$data, _forumRecord$attribut;
  const forum = app.forum;
  if (forum && typeof forum.attribute === 'function') {
    return forum.attribute(name);
  }
  const resources = (_resources = (_app$data = app.data) == null ? void 0 : _app$data.resources) != null ? _resources : [];
  const forumRecord = resources.find(r => r && r.type === 'forums');
  return forumRecord == null || (_forumRecord$attribut = forumRecord.attributes) == null ? void 0 : _forumRecord$attribut[name];
}

/** Admin-configured sitewide default scale. */
export function adminDefaultScale() {
  var _forumAttribute;
  return clampScale(parseInt((_forumAttribute = forumAttribute('linkrobinsFontScale')) != null ? _forumAttribute : String(TEXT_MIN), 10));
}

/** Admin-configured sitewide default interface size. */
export function adminDefaultUi() {
  return forumAttribute('linkrobinsFontSizerUi') === 'large';
}

/**
 * Admin-configured base sizes. Unlike scale and UI size these carry no user
 * override (no cookie): they describe the theme's starting sizes, so they are
 * read from the payload and applied once at boot.
 */
export function adminBases() {
  var _forumAttribute2, _forumAttribute3, _forumAttribute4;
  return {
    base: clampBase(parseInt((_forumAttribute2 = forumAttribute('linkrobinsFontSizerTextBase')) != null ? _forumAttribute2 : '', 10), BASE_DEFAULTS.base),
    small: clampBase(parseInt((_forumAttribute3 = forumAttribute('linkrobinsFontSizerTextSmall')) != null ? _forumAttribute3 : '', 10), BASE_DEFAULTS.small),
    title: clampBase(parseInt((_forumAttribute4 = forumAttribute('linkrobinsFontSizerTextTitle')) != null ? _forumAttribute4 : '', 10), BASE_DEFAULTS.title)
  };
}

/** Resolve the effective state from cookies, falling back to admin defaults. */
export function loadState() {
  const cookieText = getCookie(COOKIE_TEXT);
  const cookieUI = getCookie(COOKIE_UI);
  state.textScale = cookieText !== null ? clampScale(parseInt(cookieText, 10)) : adminDefaultScale();
  state.uiLarge = cookieUI !== null ? cookieUI === 'large' : adminDefaultUi();
}
export function applyText() {
  applyTextScale(state.textScale);
}
export function applyUi() {
  applyUiScale(state.uiLarge);
}
export function applyAll() {
  applyText();
  applyTextBases(adminBases());
  applyUi();
}
export function setTextScale(value) {
  state.textScale = clampScale(value);
  setCookie(COOKIE_TEXT, String(state.textScale));
  applyText();
}
export function setUiLarge(value) {
  state.uiLarge = value;
  setCookie(COOKIE_UI, value ? 'large' : 'default');
  applyUi();
}

/** Restore the admin-configured sitewide defaults and persist them. */
export function resetToDefault() {
  state.textScale = adminDefaultScale();
  state.uiLarge = adminDefaultUi();
  setCookie(COOKIE_TEXT, String(state.textScale));
  setCookie(COOKIE_UI, state.uiLarge ? 'large' : 'default');
  applyAll();
}
flarum.reg.add('linkrobins-font-sizer', 'forumState', { state: state,adminDefaultScale: adminDefaultScale,adminDefaultUi: adminDefaultUi,adminBases: adminBases,loadState: loadState,applyText: applyText,applyUi: applyUi,applyAll: applyAll,setTextScale: setTextScale,setUiLarge: setUiLarge,resetToDefault: resetToDefault, });